import React, { useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const FINISHED = ['completed', 'closed', 'deleted', 'cancelled'];

export const canEditLine = (ln, isAdmin) =>
  !FINISHED.includes(ln.wo_status) && (isAdmin || !(ln.produced_quantity > 0));

export const LineQtyEditor = ({ planId, index, line, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(String(line.quantity));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reducing = parseFloat(qty) < line.quantity;

  const save = async () => {
    setBusy(true);
    try {
      const r = await inventoryAPI.updatePlanLineQty(planId, index, { quantity: parseFloat(qty), reason });
      toast.success(r.data.message);
      (r.data.rm_warnings || []).forEach(w => toast.warning(`RM over-committed: ${w.item_name} — over by ${w.over_by}`, { duration: 9000 }));
      setOpen(false); setReason('');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not change the quantity'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5" onClick={() => { setQty(String(line.quantity)); setOpen(true); }}
        data-testid={`review-edit-qty-${index}`}>
        <Pencil className="w-3 h-3 mr-0.5" /> Qty
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 w-full flex-wrap" data-testid={`review-qty-editor-${index}`}>
      <Input type="number" min={line.produced_quantity || 0} value={qty} onChange={e => setQty(e.target.value)}
        className="h-7 w-24 text-xs" data-testid={`review-qty-input-${index}`} />
      {reducing && (
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for reducing"
          className="h-7 w-56 text-xs" data-testid={`review-qty-reason-${index}`} />
      )}
      {line.produced_quantity > 0 && (
        <span className="text-[10px] text-muted-foreground">min {line.produced_quantity} (already produced)</span>
      )}
      <Button size="sm" className="h-7 text-[10px]" onClick={save}
        disabled={busy || !(parseFloat(qty) > 0) || (reducing && !reason.trim())} data-testid={`review-qty-save-${index}`}>
        {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Save
      </Button>
      <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  );
};

export const PlanDeleteBar = ({ plan, isAdmin, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const started = plan.lines.some(l => l.produced_quantity > 0);
  const hasOpen = plan.lines.some(l => !FINISHED.includes(l.wo_status));
  if (!hasOpen || (started && !isAdmin)) return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = await inventoryAPI.cancelProductionPlan(plan.id, reason.trim());
      toast.success(r.data.message, { duration: 8000 });
      onDeleted();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete the plan'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="text-rose-600 mr-auto" onClick={() => setOpen(true)} data-testid="plan-delete">
        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete plan
      </Button>
    );
  }
  return (
    <div className="w-full border border-rose-200 bg-rose-50/50 rounded p-2 space-y-1.5" data-testid="plan-delete-panel">
      <p className="text-[11px] text-rose-800">
        {started
          ? 'Work orders not started are removed. Part-made work orders are closed at the qty already produced — that stock stays.'
          : 'The plan and its work orders are removed and their raw-material reservation is released.'}
      </p>
      <div className="flex gap-2">
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required)"
          className="h-8 text-xs" data-testid="plan-delete-reason" />
        <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={run} disabled={busy || !reason.trim()}
          data-testid="plan-delete-confirm">
          {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Delete
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};
